import React, { FC, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@trip-ux/components/error-boundary';
import { Progress } from '@trip-ux/components/progress';
import { assignQueryParam } from '@trip-ux/components/constants';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  ReduxStore,
  useActions,
  translateDateFormat,
  setTranslationDateFormat,
  getContent,
  setLocale,
  isReferenceId,
  setOrganizationCode,
  resetStore,
  getTmsTranslations,
  getTmsConfiguration,
  clearQuoteForm,
  getNavContent,
  isBroker
} from '@trip-ux/redux-store';
import { isLabelFetched, labelsSelector } from '../utils';
import { showComponent, lazyWithChunkRetry } from '@trip-ux/common';
import './booking-coverme.scss';
import { AUTH_HEADERS, COVERME_ROUTES, SUPPORTED_LOCALES } from '@trip-ux/constants';
import { getCovermeRouteEntries, CovermeRouteEntry } from './routeConfig';
import { useOrgCodeHandler } from './hooks/useOrgCodeHandler';
import { useRedirectHandler } from './hooks/useRedirectHandler';
import { useAnalyticsInit } from './hooks/useAnalyticsInit';
import { useTranslationConfigSync } from './hooks/useTranslationConfigSync';
import { useDomPurify } from './hooks/useDomPurify';
import { useReactMountSignal } from './hooks/useReactMountSignal';
import { useSafariFocusGuard } from './hooks/useSafariFocusGuard';
import buildRoutesComponent from './buildRoutesComponent';
import { CovermeMuxLayout } from './CovermeMuxLayout';

declare global {
  interface Window {
    CWC_MLCA: { dataLayer: { actions: unknown[] } };
  }
}
const TravellerDetails = lazyWithChunkRetry(() => import('./traveller-details/TravellerDetails'));
const TripDetails = lazyWithChunkRetry(() => import('./trip-details/TripDetails'));
const SelectPlan = lazyWithChunkRetry(() => import('./select-plan/SelectPlan'));
const QuoteDetails = lazyWithChunkRetry(() => import('./quote-details/QuoteDetails'));
const ContactDetails = lazyWithChunkRetry(() => import('./contact-details/ContactDetails'));
const Payment = lazyWithChunkRetry(() => import('./payment/Payment'));
const Confirmation = lazyWithChunkRetry(() => import('./confirmation/Confirmation'));
const BookingCovermeTheme = lazyWithChunkRetry(() => import('./BookingCovermeTheme'));

export type BookingCovermeProps = {
  // optional override for running explicit client/admin wrappers
  forceMode?: 'admin' | 'client';
};

// Local structural types for updateStoreConfig -- only the shape actually
// consumed by the helper is declared here, so the caller's slice of
// `ReduxStore` doesn't need to match a wider store type.
type UpdateStoreConfigActions = {
  getContent: (
    organizationCode: string,
    effectiveDate: Date,
    locale: string,
    salesChannel: string,
    contextPath: string
  ) => unknown;
};
type UpdateStoreConfigConfig = {
  options?: {
    organizationCode?: string;
    salesChannel?: string;
  };
};

const updateStoreConfig = (
  travellersProducts: unknown[] | undefined,
  actions: UpdateStoreConfigActions,
  config: UpdateStoreConfigConfig,
  currentLocale: string,
  contextPath: string
): void => {
  const hasSelectedPlan = travellersProducts?.length;
  if (hasSelectedPlan) {
    actions.getContent(
      config.options?.organizationCode ?? '',
      new Date(),
      currentLocale,
      config.options?.salesChannel ?? '',
      contextPath
    );
  }
};

// Extract effective admin flag once, coerced to a concrete boolean so that
// downstream hooks (useOrgCodeHandler, useDomPurify) and MUX layout props
// can require `boolean` rather than `boolean | undefined`.
const resolveEffectiveIsAdmin = (
  forceMode: BookingCovermeProps['forceMode'],
  cfgIsAdmin = false
): boolean => (forceMode ? forceMode === 'admin' : cfgIsAdmin);

// Resolve the route table for the current locale, falling back to English for
// any unexpected/missing value so callers can always dereference route keys.
const resolveRoutes = (currentLocale: string | undefined) => {
  const locale =
    currentLocale === SUPPORTED_LOCALES.FRENCH ? SUPPORTED_LOCALES.FRENCH : SUPPORTED_LOCALES.ENGLISH;
  return COVERME_ROUTES[locale];
};

export const BookingCoverme: FC<BookingCovermeProps> = ({ forceMode }) => {
  const {
    config,
    labels,
    translationConfig,
    translationConfigFetched,
    travellersProducts,
    userProfile,
    whiteLabel
  } = useSelector((store: ReduxStore) => ({
    config: store.config,
    labels: labelsSelector(store),
    translationConfig: labelsSelector(store, 'translationConfig'),
    translationConfigFetched: isLabelFetched(store, 'translationConfig'),
    travellersProducts: store.travellersPlan.products,
    userProfile: store.userProfile,
    whiteLabel: store.userProfile.whiteLabel
  }));

  const navigate = useNavigate();
  const lang = config.locale ?? SUPPORTED_LOCALES.ENGLISH;
  // referenceCode not used directly here; whiteLabel reference used elsewhere

  const isLIAUser = userProfile.userType === AUTH_HEADERS.LAN_ID;

  const { isAdmin: cfgIsAdmin, previousDateFormat } = config;
  const effectiveIsAdmin = resolveEffectiveIsAdmin(forceMode, cfgIsAdmin);
  const isAdmin: boolean = effectiveIsAdmin;
  const currentLocale = useMemo(() => get(config, 'locale', SUPPORTED_LOCALES.ENGLISH) as string, [config.locale]);
  const contextPath = get(config, 'options.contextPath', '') as string;
  const location = useLocation();
  const { ariaLabel: langAriaLabel, locale } = get(labels, 'page.language', {});
  const shouldShowNeedAssistance =
    !effectiveIsAdmin && !isLIAUser && !isBroker(userProfile.organizationCategory ?? '');
  const routes = resolveRoutes(currentLocale);
  const currentPath = location.pathname;

  const referenceId = isReferenceId(whiteLabel?.referenceId ?? '');

  // Centralized route config (lazy-loaded components) shared across client and admin
  const allRoutes: CovermeRouteEntry[] = useMemo(() => getCovermeRouteEntries(), []);

  // Memoized root component to avoid unnecessary re-renders
  const rootComponent = useMemo(() => {
    if (currentPath.includes(routes.CONTACT_DETAILS)) {
      return <ContactDetails />;
    }
    return <TravellerDetails />;
  }, [currentPath, routes.CONTACT_DETAILS]);

  // Mode-Specific Route Rendering: Generate complete Routes component based on mode
  const routesComponent = useMemo(() => buildRoutesComponent(allRoutes, effectiveIsAdmin, rootComponent), [allRoutes, effectiveIsAdmin, rootComponent]);

  const actions = useActions({
    translateDateFormat,
    setTranslationDateFormat,
    getContent,
    setLocale,
    setOrganizationCode,
    resetStore,
    getTmsTranslations,
    getTmsConfiguration,
    clearQuoteForm,
    getNavContent
  });

  useReactMountSignal();

  useSafariFocusGuard();

  useOrgCodeHandler({ currentPath, routes, effectiveIsAdmin, userProfile, actions, config, lang, navigate });

  useRedirectHandler(currentPath, routes, lang, navigate);

  useAnalyticsInit();

  useEffect(() => {
    updateStoreConfig(travellersProducts, actions, config, currentLocale, contextPath);
  }, [currentLocale]);

  useTranslationConfigSync(translationConfigFetched, translationConfig, actions, previousDateFormat ?? '');

  const newTabAriaLabel = get(labels, 'page.newTabAriaLabel', '');

  useDomPurify(contextPath, effectiveIsAdmin, currentLocale, newTabAriaLabel);

  const onLanguageToggle = useCallback(
    (e: React.SyntheticEvent): void => {
      e.preventDefault();
      assignQueryParam(locale);
    },
    [locale]
  );

  const showBookingCovermeTheme = effectiveIsAdmin || !referenceId;

  return (
    <ErrorBoundary>
    <div id="routes">
      <div className="sr-only" aria-live="assertive">
        {get(config, 'pageTitle')}
      </div>

      {/* MUX-based responsive layout wrapper */}
      <CovermeMuxLayout
        isAdmin={isAdmin}
        isLIAUser={isLIAUser}
        onLanguageToggle={onLanguageToggle}
        locale={locale}
        langAriaLabel={langAriaLabel}
        shouldShowNeedAssistance={shouldShowNeedAssistance}
      >
        <Suspense fallback={<Progress backdrop />}>
          {showComponent(showBookingCovermeTheme, <BookingCovermeTheme />)}
          {translationConfigFetched && routesComponent}
        </Suspense>
      </CovermeMuxLayout>
    </div>
    </ErrorBoundary>
  );
};

BookingCoverme.displayName = 'BookingCoverme';
export default BookingCoverme;
